import * as Turbo from "@hotwired/turbo";
import { render } from "preact";

import "./style/global.css";
import "./style/transitions.css";
import "@/lib/config/i18n";

import type { UUID } from "crypto";

import * as Sentry from "@sentry/browser";
import { QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "preact/compat";
import { useEffect, useMemo, useState } from "preact/hooks";
import { I18nextProvider } from "react-i18next";

import { RekazSDK } from "@rekaz/core";

import { SDKProvider, useSDK } from "@/components/context/sdk-provider";
import { CheckoutLayout } from "@/components/layout/layout";

import i18n from "@/lib/config/i18n";
import { cn } from "@/lib/utils";

import { OrderSummaryMobileSkeleton } from "./components/checkout/order-summary-mobile.skeleton";
import { OrderSummarySkeleton } from "./components/checkout/order-summary.skeleton";
import { PaymentErrorBoundary } from "./components/error/error-boundary";
import { useCart } from "./lib/hooks";
import { useLanguage } from "./lib/hooks/use-dir";
import { useSearchParams } from "./lib/hooks/use-search-params";

Sentry.init({
  dsn: "https://050a9e417e9e8df1562c3a8f59ded0f6@o4506389154234368.ingest.us.sentry.io/4509989707710464",
  sendDefaultPii: true,
  release: process.env.npm_package_version,
  integrations: [
    Sentry.browserTracingIntegration(),

    Sentry.replayIntegration()
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/platform\.rekaz\.io/,
    /^https:\/\/platform\.rekaz\.dev/
  ],

  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0
});

const Toaster = lazy(() =>
  import("@/components/ui/sonner").then((module) => ({
    default: module.Toaster
  }))
);

function PaymentSection() {
  const [components, setComponents] = useState<{
    PaymentForm: any;
    OrderSummaryMobile: any;
  } | null>(null);

  const { data: cart } = useCart();
  const hasItems = (cart?.cartItems?.length ?? 0) > 0;

  useEffect(() => {
    Promise.all([
      import("@/components/checkout/payment-form"),
      import("@/components/checkout/order-summary-mobile")
    ]).then(([paymentModule, orderMobileModule]) => {
      setComponents({
        PaymentForm: paymentModule.PaymentForm,
        OrderSummaryMobile: orderMobileModule.OrderSummaryMobile
      });
    });
  }, []);

  return (
    <Suspense fallback={OrderSummaryMobileSkeleton}>
      {components && (
        <div>
          <components.OrderSummaryMobile />
          <div
            className={cn(
              "overflow-clip transition-all duration-300 ease-in-out",
              hasItems ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
            )}
          >
            <components.PaymentForm />
          </div>
        </div>
      )}
    </Suspense>
  );
}

function OrderSummarySection() {
  const [OrderSummaryComponent, setOrderSummaryComponent] = useState<any>(null);
  const { get } = useSearchParams();
  const cartItemsCount = get<number>("cartItemsCount") || 2;

  useEffect(() => {
    import("@/components/checkout/order-summary").then((module) => {
      setOrderSummaryComponent(() => module.OrderSummary);
    });
  }, []);

  return (
    <Suspense fallback={OrderSummarySkeleton}>
      {OrderSummaryComponent && <OrderSummaryComponent />}
    </Suspense>
  );
}

function CheckoutContent() {
  return (
    <div className="grid h-full min-h-screen grid-cols-1 lg:grid-cols-12">
      <div className="flex items-stretch justify-end lg:col-span-6">
        <div className="h-full w-full max-w-xl px-4 py-8 md:px-8">
          <PaymentSection />
        </div>
      </div>
      <div className="hidden justify-start border-t bg-[#fafafa] sm:flex lg:col-span-6 lg:border-s lg:border-t-0">
        <div className="w-full max-w-lg px-4 py-8 md:px-8">
          <OrderSummarySection />
        </div>
      </div>
    </div>
  );
}

function AppWithSDKQueryClient() {
  const { get } = useSearchParams();
  const lang = get<string>("lang") || "ar";

  const { changeLanguage } = useLanguage(lang, {
    i18nInstance: i18n,
    fallbackLanguage: "ar",
    enableBodyClasses: true,
    enableDocumentAttributes: true
  });

  const { isInitialized } = useSDK();

  useEffect(() => {
    changeLanguage(lang);
  }, [lang]);

  if (!isInitialized) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
          <p>Initializing checkout...</p>
        </div>
      </div>
    );
  }

  return (
    <PaymentErrorBoundary>
      <CheckoutLayout>
        <CheckoutContent />
      </CheckoutLayout>
    </PaymentErrorBoundary>
  );
}

export default function App() {
  const { get } = useSearchParams();
  const tenant = get<string>("tenant");
  const cartId = get<UUID>("cartId");
  const lang = get<string>("lang") || "ar";

  if (!tenant) {
    throw new Error("Tenant is not provided. Please provide a tenant name.");
  }

  if (!cartId) {
    throw new Error("Cart ID is not provided in the URL.");
  }

  const sdkInstance = useMemo(() => {
    const sdk = new RekazSDK({
      tenant,
      baseUrl: import.meta.env.REKAZ_API_URL,
      locale: lang
    });
    sdk.cart.initialize(cartId);
    window.sdk = sdk;
    return sdk;
  }, [tenant, cartId, lang]);

  const queryClient = useMemo(
    () => sdkInstance.getQueryClient(),
    [sdkInstance]
  );

  if (!queryClient) {
    throw new Error("Query client is not initialized");
  }

  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <SDKProvider sdkInstance={sdkInstance}>
          <AppWithSDKQueryClient />
        </SDKProvider>
      </QueryClientProvider>

      <Suspense fallback={null}>
        <Toaster richColors />
      </Suspense>
    </I18nextProvider>
  );
}

let isAppRendered = false;

function renderApp() {
  const container = document.getElementById("checkout-content");
  if (container && !isAppRendered) {
    render(<App />, container);
    isAppRendered = true;
  }
}

renderApp();

document.addEventListener("turbo:load", renderApp);
document.addEventListener("turbo:render", renderApp);

window.Turbo = Turbo;
